import {useMemo} from 'react';

import {
  ArtifactAlias,
  useArtifactVersionAliasesQuery,
  useArtifactVersionTagsQuery,
  useRegistryProjectMembersQuery,
  WorkflowsAdminType,
} from '../../generated/graphql';
import {useTeamMember} from '../../state/graphql/teamMembers';
import {useViewer} from '../../state/viewer/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {checkRegistryProject} from '../Registries/common/utils';
import {
  getIsViewerRegistryAdmin,
  getRegistryMembers,
  getRegistryMembersMap,
  getRegistryTeamMembers,
} from '../Registries/RegistrySettings/RegistryMembersTable/utils';
import {mapGQLTags} from '../TagAssignment';

export const useArtifactMembershipAliases = (
  artifactCollectionID: string,
  versionIndex: number
) => {
  const viewer = useViewer();

  const {data, loading, error, refetch} = useArtifactVersionAliasesQuery({
    variables: {
      artifactCollectionID,
      versionAlias: `v${versionIndex}`,
      adminType: WorkflowsAdminType.Model,
    },
    context: propagateErrorsContext(),
  });

  const protectedAliases = useMemo(() => {
    const {isRegistryProject} = checkRegistryProject(
      data?.artifactCollection?.project?.name ?? ''
    );
    const legacyProtectedAliases =
      data?.artifactCollection?.project?.entity?.protectedAliases ?? [];
    const registryProtectedAliases =
      data?.artifactCollection?.project?.protectedArtifactAliases.map(
        alias => alias.alias
      ) ?? [];
    return isRegistryProject
      ? registryProtectedAliases
      : legacyProtectedAliases;
  }, [data]);

  const {entityName, projectName} = useMemo(() => {
    return {
      entityName: data?.artifactCollection?.project?.entity?.name ?? '',
      projectName: data?.artifactCollection?.project?.name ?? '',
    };
  }, [data]);

  const {viewerIsTeamAdmin} = useTeamMember({
    skip: entityName == null,
    viewer,
    entityName,
  });

  const isRegistryProject = useMemo(() => {
    const {isRegistryProject} = checkRegistryProject(projectName ?? '');
    return isRegistryProject;
  }, [projectName]);

  const registryProjectMembersQuery = useRegistryProjectMembersQuery({
    skip: entityName == null || projectName == null || !isRegistryProject,
    variables: {
      entityName,
      projectName,
    },
  });

  const isViewerRegistryAdmin = useMemo(() => {
    if (!isRegistryProject) {
      return (
        data?.artifactCollection?.project?.entity?.workflowsAdmins?.some(
          (admin: any) => admin.username === viewer?.username
        ) || viewerIsTeamAdmin
      );
    }
    const registryTeamMembers = getRegistryTeamMembers(
      registryProjectMembersQuery.data?.project?.teamMembers ?? []
    );
    // HACK: we don't need to get the members org role to determine if they are
    // a registry admin, so we just pass in an empty array for the organization
    // members
    const registryMembers = getRegistryMembers(
      [],
      registryProjectMembersQuery.data?.project?.members ?? []
    );
    return getIsViewerRegistryAdmin(
      getRegistryMembersMap(registryMembers),
      getRegistryMembersMap(registryTeamMembers),
      viewer
    );
  }, [
    data?.artifactCollection?.project?.entity?.workflowsAdmins,
    isRegistryProject,
    registryProjectMembersQuery.data?.project?.members,
    registryProjectMembersQuery.data?.project?.teamMembers,
    viewer,
    viewerIsTeamAdmin,
  ]);

  // all aliases that are used in the collection
  const availableAliases = useMemo(() => {
    if (loading) {
      return [];
    }
    // only show protected aliases to registry admins
    const availableProtectedAliases =
      isViewerRegistryAdmin && protectedAliases.length > 0
        ? protectedAliases.map(alias => ({
            alias,
            isProtected: true,
          }))
        : [];

    const existingAliases = (data?.artifactCollection?.aliases?.edges
      .map(edge => edge.node)
      .filter(alias => alias != null) ?? []) as Pick<ArtifactAlias, 'alias'>[];
    const filteredExistingAliases = existingAliases
      .filter(
        (alias: Pick<ArtifactAlias, 'alias'>) =>
          !availableProtectedAliases.some(a => a.alias === alias.alias)
      )
      .map((alias: Pick<ArtifactAlias, 'alias'>) => ({
        alias: alias.alias,
        isProtected: false,
      }));
    return filteredExistingAliases?.concat(availableProtectedAliases) ?? [];
  }, [
    loading,
    isViewerRegistryAdmin,
    protectedAliases,
    data?.artifactCollection?.aliases?.edges,
  ]);

  return {
    artifactName: data?.artifactCollection?.name,
    projectName: data?.artifactCollection?.project?.name,
    entityName: data?.artifactCollection?.project?.entity?.name,
    artifactId: data?.artifactCollection?.artifactMembership?.artifact?.id,
    artifactTypeName:
      data?.artifactCollection?.artifactMembership?.artifact?.artifactType
        ?.name,
    artifactIsPortfolio:
      data?.artifactCollection?.__typename === 'ArtifactPortfolio',
    artifactVersionAliases:
      data?.artifactCollection?.artifactMembership?.aliases ?? [],
    protectedAliases,
    availableAliases,
    isViewerRegistryAdmin,
    loading,
    error,
    refetch,
  };
};

export const useArtifactMembershipTags = (
  artifactCollectionID: string,
  versionIndex: number
) => {
  const {data, loading, error, refetch} = useArtifactVersionTagsQuery({
    variables: {
      artifactCollectionID,
      versionAlias: `v${versionIndex}`,
    },
  });

  return {
    artifactName: data?.artifactCollection?.name,
    projectName: data?.artifactCollection?.project?.name,
    entityName: data?.artifactCollection?.project?.entity?.name,
    artifactId: data?.artifactCollection?.artifactMembership?.artifact?.id,
    tags: mapGQLTags(
      data?.artifactCollection?.artifactMembership?.artifact?.tags ?? []
    ),
    loading,
    error,
    refetch,
  };
};
